import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiFileText, 
  FiBook, 
  FiTarget, 
  FiAward, 
  FiCalendar,
  FiFolder,
  FiHeadphones,
  FiLayers,
  FiHome,
  FiDollarSign,
  FiBookOpen,
  FiShield,
  FiMessageSquare,
  FiUser,
  FiUsers,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiCreditCard
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import NaatiLogo from './NaatiLogo';
import './Sidebar.css';

interface MenuItem {
  id: string;
  title: string;
  icon?: IconType;
  path?: string;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: FiGrid,
    path: '/dashboard'
  },
  {
    id: 'test-rules',
    title: 'Test Rules',
    icon: FiFileText,
    path: '/test-rules'
  },
  {
    id: 'material',
    title: 'Material',
    icon: FiBook,
    children: [
      {
        id: 'dialogue-categories',
        title: 'Dialogue Categories',
        icon: FiFolder,
        path: '/material/dialogue-categories'
      },
      {
        id: 'structured-segments',
        title: 'Structural Segments',
        icon: FiLayers,
        path: '/material/structured-segments'
      },
      {
        id: 'podcast',
        title: 'Podcast',
        icon: FiHeadphones,
        path: '/material/podcast'
      }
    ]
  },
  {
    id: 'mock-service',
    title: 'Mock Service',
    icon: FiTarget,
    path: '/mock-service'
  },
  {
    id: 'subscription',
    title: 'Get Subscription',
    icon: FiCreditCard,
    path: '/subscription/purchase'
  },
  {
    id: 'booking',
    title: 'Booking',
    icon: FiCalendar,
    path: '/booking'
  },
  {
    id: 'online-courses',
    title: 'Online Courses',
    icon: FiAward,
    children: [
      {
        id: 'vip-feedback',
        title: 'VIP Feedback',
        icon: FiMessageSquare,
        path: '/online-courses/vip-feedback'
      },
      {
        id: 'private',
        title: 'Private',
        icon: FiUser,
        path: '/online-courses/private'
      },
      {
        id: 'workshop',
        title: 'Workshop',
        icon: FiUsers,
        path: '/online-courses/workshop'
      }
    ]
  }
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
  const { handleMenuClick, showTutorial, waitingForClick } = useTutorial();

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };


  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isExpanded = (itemId: string) => expandedItems.includes(itemId);

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const expanded = isExpanded(item.id);
    const active = isActive(item.path);

    return (
      <div key={item.id} className={`menu-item-wrapper level-${level}`}>
        <motion.div
          className={`menu-item ${active ? 'active' : ''} ${hasChildren ? 'has-children' : ''}`}
          onClick={() => handleItemClick(item)}
          whileHover={{ backgroundColor: 'rgba(230, 57, 70, 0.1)' }}
          whileTap={{ scale: 0.98 }}
          data-menu-id={item.id}
        >
          <div className="menu-item-content">
            {item.icon && (
              <span className="menu-icon">
                {React.createElement(item.icon as React.ComponentType<{ size?: number }>, { size: 18 })}
              </span>
            )}
            <span className="menu-title">{item.title}</span>
          </div>
          {hasChildren && (
            <motion.span
              className="menu-arrow"
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {React.createElement(FiChevronDown as React.ComponentType<{ size?: number }>, { size: 16 })}
            </motion.span>
          )}
        </motion.div>

        <AnimatePresence>
          {hasChildren && expanded && (
            <motion.div
              className="submenu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="submenu-content">
                {item.children!.map(child => renderMenuItem(child, level + 1))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.children) {
      toggleExpand(item.id);
    } else if (item.path) {
      navigate(item.path);
      // Close mobile sidebar when navigating
      if (window.innerWidth <= 768) {
        setIsMobileOpen(false);
      }
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && window.innerWidth <= 768 && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
      <motion.div
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
      <div className="sidebar-header">
        <motion.div
          className="sidebar-logo"
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate('/dashboard')}
        >
          <NaatiLogo
            className="sidebar-logo-wrap"
            imgClassName="sidebar-logo-img"
            responsive
          />
          {!isCollapsed && (
            <span className="logo-subtitle">Naatilaboratory</span>
          )}
        </motion.div>
        <button
          className="sidebar-toggle"
          onClick={() => {
            if (window.innerWidth <= 768) {
              setIsMobileOpen(false);
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {React.createElement(
            (isCollapsed ? FiChevronRight : FiChevronLeft) as React.ComponentType<{ size?: number }>,
            { size: 20 }
          )}
        </button>
      </div>

      <nav className="sidebar-nav">
        {(!isCollapsed || window.innerWidth <= 768) && menuItems.map(item => renderMenuItem(item))}
        {isCollapsed && window.innerWidth > 768 && (
          <div className="collapsed-menu">
            {menuItems.map(item => (
              <div
                key={item.id}
                className="collapsed-menu-item"
                onClick={() => {
                  if (item.path) {
                    navigate(item.path);
                  } else if (item.children) {
                    toggleExpand(item.id);
                  }
                }}
                title={item.title}
              >
                {item.icon && React.createElement(
                  item.icon as React.ComponentType<{ size?: number }>,
                  { size: 20 }
                )}
              </div>
            ))}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        {!isCollapsed && (
          <div className="footer-info">
            <p className="footer-text">NAATI Assessment Platform</p>
            <p className="footer-copyright">© 2025 All rights reserved</p>
          </div>
        )}
      </div>
    </motion.div>
    </>
  );
};

export default Sidebar;
